import { Link } from "react-router-dom";
import { Bookmark, MapPin, CurrencyGbp, CurrencyDollar } from "@phosphor-icons/react";
import { timeAgo } from "@/lib/utils.jobs";
import { BrandBadge } from "@/components/BrandBadge";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export function JobCard({ job, saved, onToggleSaved, index = 0 }) {
  const { user } = useAuth();

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || user === false) {
      toast.error("Please sign in to save jobs");
      return;
    }
    try {
      if (saved) {
        await api.delete(`/me/saved-jobs/${job.id}`);
        toast.success("Removed from saved jobs");
      } else {
        await api.post(`/me/saved-jobs/${job.id}`);
        toast.success("Job saved");
      }
      onToggleSaved?.(job.id, !saved);
    } catch {
      toast.error("Something went wrong");
    }
  };

  const tags = [job.job_type, job.location?.toLowerCase().includes("remote") ? "Remote" : null]
    .filter(Boolean);

  const Currency = job.country === "US" ? CurrencyDollar : CurrencyGbp;

  return (
    <Link
      to={`/jobs/${job.id}`}
      className="group block animate-fade-up rounded-xl border border-border bg-card p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-lg"
      style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start gap-4">
        <BrandBadge size={48} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-foreground transition-colors group-hover:text-primary">
                {job.title}
              </h3>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                Managed by The Vacancy Network
              </p>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(job.posted_at)}</span>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {job.location && (
              <span className="flex items-center gap-1">
                <MapPin size={14} weight="fill" className="text-accent" /> {job.location}
              </span>
            )}
            {job.salary && (
              <span className="flex items-center gap-1">
                <Currency size={14} weight="bold" className="text-accent" /> {job.salary}
              </span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {tags.map((t, i) => (
              <span
                key={i}
                className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-medium text-secondary-foreground"
              >
                {t}
              </span>
            ))}
            {job.category && (
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-medium text-primary">
                {job.category}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={toggleSave}
          className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary"
          data-testid={`save-job-btn-${job.id}`}
          aria-label="Save job"
        >
          <Bookmark size={20} weight={saved ? "fill" : "regular"} className={saved ? "text-primary" : ""} />
        </button>
      </div>
    </Link>
  );
}
